import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { saveAsWorkflow } from "../../api/content/workflowSavesApi";
import { listWorkflows } from "../../api/content/workflowsApi";
import type { ThreadEntry } from "../../api/threads/liveMessages";
import { readEntries } from "../../api/threads/threadsApi";
import { appPaths } from "../../shared/routing/appPaths";
import ErrorNotice from "../../errors/ErrorNotice";
import { useLoad } from "../../shared/loading/useLoad";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import ModalShell from "../../ui/overlays/ModalShell";
import { heldWorkflow } from "./heldName";
import { saveCandidates, type SaveCandidate } from "./saveCandidates";
import Chip from "../../ui/feedback/Chip";
import { stepKindText } from "../workflows/inputCards/typeWords";
import Field from "../../ui/forms/Field";
import LoadStatus from "../../ui/feedback/LoadStatus";

const PAGE_SIZE = 200;

async function wholePath(threadId: string, signal: AbortSignal): Promise<readonly ThreadEntry[]> {
  const entries: ThreadEntry[] = [];
  for (;;) {
    const page = await readEntries(threadId, entries.at(-1)?.number ?? 0, PAGE_SIZE, signal);
    entries.push(...page.entries);
    if (page.entries.length === 0 || (entries.at(-1)?.number ?? 0) >= page.lastEntryNumber) {
      return entries;
    }
  }
}

interface Pick {
  readonly picked: boolean;
  readonly start: "automatic" | "on_request";
}

/**
 * "Save as Workflow" (FR-WF-3): pick the steps and replies of this Thread's path that made
 * the better path; they are proposed as a Workflow change set, and its review opens. A name a
 * current Workflow holds saves as its next version, or as the project's version of a library's
 * (workflow-names WN-4): one line under the name says which.
 */
export default function SaveAsWorkflowDialog({ projectId, threadId, onClose }: { projectId: string; threadId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const load = useCallback((signal: AbortSignal) => wholePath(threadId, signal), [threadId]);
  const { state, reload } = useLoad(load);
  const loadOffered = useCallback((signal: AbortSignal) => listWorkflows(projectId, null, signal), [projectId]);
  const { state: offered } = useLoad(loadOffered);
  const submission = useSubmission();
  const [name, setName] = useState("");
  const held = offered.status === "loaded" ? heldWorkflow(offered.value.workflows, name) : null;
  const [picks, setPicks] = useState<Readonly<Record<number, Pick>>>({});
  const candidates = state.status === "loaded" ? saveCandidates(state.value) : [];
  const pickOf = (candidate: SaveCandidate): Pick => picks[candidate.entryNumber] ?? { picked: true, start: candidate.defaultStart };
  const chosen = candidates.filter((candidate) => pickOf(candidate).picked);
  const lastEntry = state.status === "loaded" ? (state.value.at(-1)?.number ?? 1) : 1;

  const save = () => {
    submission.submit(async () => {
      const changeSetId = await saveAsWorkflow(threadId, {
        name: name.trim(),
        description: t("workflowSaving.description"),
        savedAt: lastEntry,
        steps: chosen.map((candidate) => ({ entry: candidate.entryNumber, key: null, start: pickOf(candidate).start })),
      });
      void navigate(appPaths.review(changeSetId));
    });
  };

  return (
    <ModalShell
      title={t("workflowSaving.title")}
      subtitle={t("workflowSaving.subtitle")}
      onClose={onClose}
      size="2xl"
      footer={
        <div className="flex justify-end gap-2">
          <AppButton variant="secondary" size="sm" onClick={onClose}>{t("common.cancel")}</AppButton>
          <AppButton
            variant="primary"
            size="sm"
            disabled={name.trim() === "" || chosen.length === 0 || submission.state.status === "submitting"}
            onClick={save}
          >
            {t("workflowSaving.save")}
          </AppButton>
        </div>
      }
    >
      <div className="space-y-3">
        <Field
          label={t("workflowSaving.name")}
          hint={held === null ? undefined : t(held.library === null ? "workflowSaving.nextVersion" : "workflowSaving.projectVersion", { name: held.name })}
        >
          {(control) => (
            <input
              {...control}
              className="app-input w-full rounded border px-2 py-1"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          )}
        </Field>
        <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />
        {state.status === "loaded" && candidates.length === 0 ? <p className="app-text-muted text-sm">{t("workflowSaving.nothing")}</p> : null}
        <ol className="space-y-1">
          {candidates.map((candidate) => {
            const pick = pickOf(candidate);
            return (
              <li key={candidate.entryNumber} className="app-surface flex flex-wrap items-center gap-2 rounded border px-2 py-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={pick.picked}
                  aria-label={t("workflowSaving.pick", { number: candidate.entryNumber })}
                  onChange={(event) => {
                    setPicks({ ...picks, [candidate.entryNumber]: { ...pick, picked: event.target.checked } });
                  }}
                />
                <Chip>{candidate.kind === "human_step" ? stepKindText("human", t) : t(`workflowSaving.kinds.${candidate.kind}`)}</Chip>
                <span className="min-w-0 flex-1 truncate">
                  #{candidate.entryNumber} {candidate.stepKey ?? candidate.preview}
                </span>
                <select
                  className="app-select rounded border px-1 py-0.5 text-xs"
                  value={pick.start}
                  aria-label={t("workflowSaving.start")}
                  onChange={(event) => {
                    setPicks({ ...picks, [candidate.entryNumber]: { ...pick, start: event.target.value === "automatic" ? "automatic" : "on_request" } });
                  }}
                >
                  <option value="on_request">{t("workflowSaving.starts.on_request")}</option>
                  <option value="automatic">{t("workflowSaving.starts.automatic")}</option>
                </select>
              </li>
            );
          })}
        </ol>
        {submission.state.status === "failed" ? <ErrorNotice error={submission.state.error} /> : null}
      </div>
    </ModalShell>
  );
}
